import * as models from 'pkg/api/models';
import { AuthorType } from 'pkg/api/models/author';
import type { Author } from 'pkg/api/models/author';
import type { FieldValue } from 'pkg/api/models/form_field';
import type { User } from 'pkg/api/models/user';
import { UserSex, MetaField } from 'pkg/api/models/user';
import { fullName, initials } from 'pkg/api/models/user/fmt';
import { UserAddressTypes } from 'pkg/api/models/user_address';
import type { UserAddress } from 'pkg/api/models/user_address';

export function getMeta<T = string>(user: User, key: MetaField): T {
	return user.meta?.find((meta: any) => meta.key === key)?.value as T;
}

/**
 * The options a `user_sex` form field must carry, in order.
 *
 * These labels are raw sentinels the API matches on literally — it requires all
 * four and rejects any other set — so despite sitting next to the translation
 * helpers below they must never be wrapped in `t()`. Translate them for display
 * with `stringToTranslatedSexString` instead.
 */
export const userSexFieldValues: FieldValue[] = [
	{ value: String(UserSex.Unknown), label: 'unknown' },
	{ value: String(UserSex.Male), label: 'male' },
	{ value: String(UserSex.Female), label: 'female' },
	{ value: String(UserSex.NotApplicable), label: 'not_applicable' },
];

export function sexString(user: User): string {
	let sexString = 'not_set';

	if (user.sex === UserSex.Male) {
		sexString = 'male';
	} else if (user.sex === UserSex.Female) {
		sexString = 'female';
	} else if (user.sex === UserSex.NotApplicable) {
		sexString = 'not_applicable';
	}

	return sexString;
}

export function stringToSex(value: string): UserSex | undefined {
	switch (value.toLowerCase()) {
		case 'male':
			return UserSex.Male;
		case 'female':
			return UserSex.Female;
		case 'notApplicable':
			return UserSex.NotApplicable;
		default:
			return undefined;
	}
}

export function birthYear(user: User): number {
	return user.birthDate ? new Date(user.birthDate).getFullYear() : null;
}

export function getEstimatedHeight(user: User): number {
	const checkFormat = (rawValue: string): number => {
		rawValue = `${Math.round(parseFloat(rawValue))}`;
		if (Number.isNaN(rawValue.charAt(1))) {
			rawValue = rawValue.replace(/\D/g, '');
		}
		return Number.parseInt(rawValue, 10);
	};

	const parents = {
		mother: checkFormat(
			user.meta?.find((m) => m.key === 'mother-height').value || '0'
		),
		father: checkFormat(
			user.meta?.find((m) => m.key === 'father-height').value || '0'
		),
	};

	if (!parents.mother || !parents.father) {
		return 0;
	}

	if (!user.sex || user.sex === UserSex.NotApplicable) {
		return 0;
	}

	return (
		(parents.mother + parents.father + (user.sex === UserSex.Male ? 13 : -13)) /
		2
	);
}

export function legalGuardianFor(user: User): string {
	return getMeta(user, MetaField.LegalGuardian);
}

export function allergies(user: User): string {
	return getMeta(user, MetaField.Allergies);
}

export function misc(user: User): string {
	return getMeta(user, MetaField.Allergies);
}

function findBillingAddress(user: User): UserAddress | undefined {
	return user.addresses?.find((a) => a.type === UserAddressTypes.Billing);
}

export function getBillingEmail(user: User) {
	if (user.billingUser?.id) {
		return (
			findBillingAddress(user.billingUser)?.email || user.billingUser.email
		);
	}
	return findBillingAddress(user)?.email || user.email;
}

export function getBillingAddress(user: User): UserAddress {
	return user.addresses?.length > 0
		? user.addresses.filter((a) => a.type === UserAddressTypes.Billing)[0]
		: ({
				firstName: user.firstName,
				lastName: user.lastName,
				email: user.email,
				phoneNumber: user.phoneNumber,
				address: user.address,
				city: user.city,
				country: user.country,
				postalCode: user.postalCode,
			} as UserAddress);
}

export function getMissingRequiredUserFields(
	user: models.user.User
): models.userFields.UserField[] {
	return user?.missingRequiredFields || [];
}

export function getFieldValuesAsPayload(fields = [] as unknown) {
	const payloadFields = [];

	for (const [keyWithType, value] of Object.entries(fields)) {
		const [type, key] = keyWithType.split(':');

		switch (type) {
			case models.userFields.UserFieldType.Attachment:
				if (!value) {
					payloadFields.push({
						key,
						value: '',
					});
				} else {
					payloadFields.push({
						key,
						attachmentId: Number.parseInt(value, 10),
					});
				}

				break;
			case models.userFields.UserFieldType.MultiChoice:
				if (!value || value.length === 0) {
					payloadFields.push({
						key,
						value: '',
					});
				} else {
					for (const option of value) {
						payloadFields.push({
							key,
							value: option,
						});
					}
				}
				break;
			default:
				payloadFields.push({
					key,
					value,
				});
				break;
		}
	}

	return payloadFields;
}

export function toAuthor(user: models.user.User): Author {
	return {
		type: AuthorType.User,
		name: fullName(user),
		profileImageUrl: user.profileImageUrl,
		initials: initials(user),
		firstName: user.firstName,
		lastName: user.lastName,
	};
}
